import { Badge, Box, Center, Stack, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import type { ReactNode } from 'react';

type LockedOverlayProps = {
  /** Short explanation: «Доступно по подписке», «Доступно владельцам игры». */
  reason: string;
  children: ReactNode;
  /** Optional short note under the reason. */
  hint?: string;
  /**
   * Куда ведёт закрытая карточка — на страницу материала, где сказано, как его
   * открыть, и стоит кнопка тарифа или покупки. Без неё карточка — тупик.
   */
  onOpen?: () => void;
};

/**
 * Locked content is dimmed and explained, never hidden (§19).
 *
 * Deliberately calm: no paywall modal, no upsell pressure — the prototype only
 * has to make the access rule visible and testable.
 */
export function LockedOverlay({ reason, hint, children, onOpen }: LockedOverlayProps) {
  return (
    <Box
      pos="relative"
      style={{ borderRadius: 'var(--radius-card)', overflow: 'hidden', cursor: onOpen ? 'pointer' : undefined }}
      onClick={onOpen}
      onKeyDown={onOpen ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      } : undefined}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `${reason}. Открыть страницу материала` : undefined}
    >
      <Box style={{ filter: 'grayscale(0.7)', opacity: 0.45 }} aria-hidden>
        {children}
      </Box>

      <Center
        pos="absolute"
        inset={0}
        p="md"
        style={{ background: 'var(--locked-overlay)' }}
      >
        <Stack align="center" gap={6}>
          <Badge
            size="lg"
            radius="sm"
            variant="filled"
            color="dark"
            leftSection={<IconLock size={13} />}
            style={{ background: 'rgba(255,255,255,0.92)', color: 'var(--text-primary)' }}
          >
            {reason}
          </Badge>
          {hint ? (
            <Text size="xs" c="white" ta="center" maw={220}>
              {hint}
            </Text>
          ) : null}
        </Stack>
      </Center>
    </Box>
  );
}
